import debounce from 'lodash/debounce'

import { AUTOCOMPLETE_CONFIG } from '@/constants/autocomplete'

export const normalizeExecuteError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error))

/**
 * Debounced query execution: one burst and one in-flight `execute` for all callers;
 * serializes waiters so a single network batch runs and newest input wins.
 */
export const createDebouncedQuery = <T>(
	execute: (query: string) => Promise<T>
): ((query: string) => Promise<T>) => {
	let latestQuery = ''
	const waiters: Array<{
		resolve: (value: T) => void
		reject: (error: Error) => void
	}> = []
	let inFlight = false

	const flush = async (): Promise<void> => {
		if (inFlight || waiters.length === 0) {
			return
		}

		inFlight = true
		const query = latestQuery
		const batch = [...waiters]
		waiters.length = 0

		try {
			const result = await execute(query)
			const hasNewerWaiters = waiters.length > 0 || latestQuery !== query

			if (hasNewerWaiters) {
				waiters.unshift(...batch)
				return
			}

			batch.forEach((waiter) => waiter.resolve(result))
		} catch (error) {
			const normalizedError = normalizeExecuteError(error)
			batch.forEach((waiter) => waiter.reject(normalizedError))
		} finally {
			inFlight = false
			if (waiters.length > 0) {
				scheduleFlush()
			}
		}
	}

	const scheduleFlush = debounce(() => {
		void flush()
	}, AUTOCOMPLETE_CONFIG.DEBOUNCE_DELAY)

	return (query: string): Promise<T> => {
		latestQuery = query

		return new Promise<T>((resolve, reject) => {
			waiters.push({ resolve, reject })
			scheduleFlush()
		})
	}
}
